/**
 * Site device credentials -- server-state hooks and response types.
 *
 * Backend: api/v2/site_device_credentials.py. Contract: docs/design/site-device-credentials.md.
 * Own module (like siteVlans.ts) so hooks.ts and queryKeys.ts stay untouched; the query keys below
 * use the same scope-prefixed shape as qk.sites.* so a scope switch still separates caches.
 *
 * SECRET HANDLING: the two password-bearing calls (reveal, bootstrap) are plain async functions,
 * NOT react-query mutations -- a mutation keeps `variables`/`data` in the shared cache, which would
 * park the typed switch password or the revealed one in memory beyond the component that shows it.
 * The component holds them in local state and clears them (timer / close / unmount).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import { useSession } from "@/stores/session";
import type { AccessLike, ExcludedReason, LoginState } from "@/lib/devicePassword";

/* ---- types ------------------------------------------------------------------------------ */

export interface CredentialSummary {
  admin_password_set: boolean;
  admin_rotated_at: string | null;
  /* The fields below are present for msp_admin only. */
  admin_username?: string | null;
  admin_generation?: number;
  admin_rotated_by?: string | null;
  service_username?: string | null;
  service_password_set?: boolean;
  service_created_at?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface CredentialItem {
  code: string;
  message: string;
  serial: string | null;
  actions: ("bootstrap" | "check_login")[];
}

export interface SwitchAccess extends AccessLike {
  gateway_serial: string | null;
  reason: string | null;
  error: { code: string; message: string } | null;
}

export interface SwitchLogin {
  state: LoginState;
  source: "site" | "env";
  username: string | null;
  checked_at: string | null;
  error: string | null;
}

export interface PlanSession {
  as_user: string;
  purpose: string;
  /** Password tokens are already "********" server-side. */
  commands: string[];
}

export interface CredentialSwitch {
  serial: string;
  name: string;
  model: string | null;
  online: boolean;
  access: SwitchAccess;
  login: SwitchLogin;
  admin_current: boolean;
  service_installed: boolean;
  actions: ("rotate_admin" | "install_service_account")[];
  sessions: PlanSession[];
  refusal: CredentialItem | null;
  note: string | null;
}

export interface SessionStep {
  step: string;
  as_user: string;
  ok: boolean;
  commands: string[];
  error: string | null;
  stage: string | null;
  saved: boolean | null;
  partial: boolean;
  rejected: { index: number | null; command: string | null; error: string }[];
  login: LoginState;
}

export interface ApplyDevice {
  serial: string;
  name: string;
  status: "pending" | "running" | "succeeded" | "failed";
  actions: string[];
  message: string | null;
  steps: SessionStep[];
}

export interface LastApply {
  status: "running" | "succeeded" | "failed";
  started_at: string | null;
  finished_at: string | null;
  started_by: string | null;
  devices: ApplyDevice[];
}

/** GET /sites/{id}/device-credentials -- read-only callers get `credentials` only. */
export interface DeviceCredentialsView {
  site_id: number;
  credentials: CredentialSummary;
  default_admin_username?: string;
  key_configured?: boolean;
  key_mismatch?: boolean;
  cli_enabled?: boolean;
  password_policy?: { min_length: number; max_length: number; forbidden_characters: string };
  last_apply?: LastApply | null;
  switches?: CredentialSwitch[];
  /** Switches this card leaves out on purpose, with the reason (OP#1856: SonicFi RSW, no CLI). */
  excluded_switches?: ExcludedSwitch[];
  plan_refusals?: CredentialItem[];
}

/** A switch at the site that the credential apply never targets, named so the counts agree. */
export interface ExcludedSwitch {
  serial: string;
  name: string;
  model: string | null;
  online: boolean;
  reason: ExcludedReason;
  note: string;
}

export interface CredentialPlan {
  site_id: number;
  plan_token: string;
  can_apply: boolean;
  switch_count: number;
  refusals: CredentialItem[];
  warnings: CredentialItem[];
  switches: CredentialSwitch[];
  excluded_switches?: ExcludedSwitch[];
}

export interface BootstrapResult {
  serial: string;
  ok: boolean;
  message: string;
  steps: SessionStep[];
}

/* ---- keys ------------------------------------------------------------------------------- */

const seg = (v: string | null | undefined): string => v ?? "_";
const keys = {
  view: (orgId: string | null, siteId: string) => ["v2", seg(orgId), "sites", seg(siteId), "device-credentials"] as const,
  plan: (orgId: string | null, siteId: string) =>
    ["v2", seg(orgId), "sites", seg(siteId), "device-credentials", "plan"] as const,
};

/* ---- hooks ------------------------------------------------------------------------------ */

/** The credential view. Polls every 2 s while an Apply is running so per-switch rows move. */
export function useDeviceCredentials(siteId: string) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: keys.view(orgId, siteId),
    queryFn: ({ signal }) => api.get<DeviceCredentialsView>(`/sites/${siteId}/device-credentials`, undefined, signal),
    enabled: !!siteId,
    refetchInterval: (query) => (query.state.data?.last_apply?.status === "running" ? 2000 : false),
  });
}

/** READ-ONLY plan for the Apply preview; never cached past the dialog. */
export function useDeviceCredentialPlan(siteId: string, enabled: boolean) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: keys.plan(orgId, siteId),
    queryFn: ({ signal }) => api.get<CredentialPlan>(`/sites/${siteId}/device-credentials/plan`, undefined, signal),
    enabled: enabled && !!siteId,
    staleTime: 0,
    gcTime: 0,
  });
}

function useInvalidate(siteId: string) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return () => {
    void qc.invalidateQueries({ queryKey: keys.view(orgId, siteId) });
    void qc.invalidateQueries({ queryKey: keys.plan(orgId, siteId) });
  };
}

/** Invalidate the view + plan (for the plain-async reveal/bootstrap calls). */
export const useInvalidateDeviceCredentials = useInvalidate;

/** PUT a new username/password. The caller resets the mutation on success (drops `variables`). */
export function useSaveDeviceCredentials(siteId: string) {
  const invalidate = useInvalidate(siteId);
  return useMutation({
    mutationFn: (body: { admin_username?: string; admin_password?: string }) =>
      api.put<DeviceCredentialsView>(`/sites/${siteId}/device-credentials`, body),
    onSuccess: invalidate,
  });
}

/** POST apply {plan_token}. 202 {last_apply}; the view then polls. */
export function useApplyDeviceCredentials(siteId: string) {
  const invalidate = useInvalidate(siteId);
  return useMutation({
    mutationFn: (planToken: string) =>
      api.post<{ last_apply: LastApply }>(`/sites/${siteId}/device-credentials/apply`, { plan_token: planToken }),
    onSettled: invalidate,
  });
}

/** POST check/{serial}: prove Meridian's current login with one empty session. */
export function useCheckSwitchLogin(siteId: string) {
  const invalidate = useInvalidate(siteId);
  return useMutation({
    mutationFn: (serial: string) =>
      api.post<{ serial: string; login: LoginState; step: SessionStep }>(
        `/sites/${siteId}/device-credentials/check/${encodeURIComponent(serial)}`,
      ),
    onSettled: invalidate,
  });
}

/** POST reveal -- plain async on purpose (see module note). Audited server-side. */
export function revealDevicePassword(siteId: string) {
  return api.post<{ username: string; password: string; hide_after_seconds: number }>(
    `/sites/${siteId}/device-credentials/reveal`,
  );
}

/** POST bootstrap/{serial} -- plain async on purpose: the typed password is never cached. */
export function bootstrapSwitch(siteId: string, serial: string, currentAdminPassword: string, adminUsername?: string) {
  return api.post<BootstrapResult>(`/sites/${siteId}/device-credentials/bootstrap/${encodeURIComponent(serial)}`, {
    current_admin_password: currentAdminPassword,
    ...(adminUsername ? { admin_username: adminUsername } : {}),
  });
}
